import React from 'react';
import { FaPlay, FaRedoAlt } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import ThemedSelect from '../../common/ThemedSelect';

// Unified filter bar: the ONE filter surface for Route Summary, rendered
// identically before and after a summary exists (ST-00124). No collapse modes,
// no alternate layouts — the bar never morphs, only the button label changes
// (Generate → Update) once results are on screen.
const toDateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const FieldLabel = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-[11px] font-medium text-slate-500 mb-1">
    {children}
  </label>
);

const FilterControls = ({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  minWeight,
  setMinWeight,
  selectedGroupings,
  setSelectedGroupings,
  groupings,
  onGenerate,
  loading,
  loadingGroupings,
  hasResults,
}) => {
  const selected = selectedGroupings || [];
  const selectedIds = selected.map((g) => g.id);
  const canGenerate = selected.length > 0 && !loading && !loadingGroupings;

  return (
    <div className="bg-surface rounded-xl border border-slate-200 shadow-sm px-4 py-3">
      <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
        {/* Analysis period */}
        <div className="w-full sm:w-auto">
          <FieldLabel>Period</FieldLabel>
          <div className="w-full sm:w-[264px]">
            <UnifiedDateRangePicker
              startDate={startDate ? new Date(startDate + 'T00:00:00') : null}
              onStartDateChange={(date) => date && setStartDate(toDateString(date))}
              endDate={endDate ? new Date(endDate + 'T00:00:00') : null}
              onEndDateChange={(date) => date && setEndDate(toDateString(date))}
              disabled={loading}
            />
          </div>
        </div>

        {/* Minimum weight */}
        <div className="w-[104px]">
          <FieldLabel htmlFor="minWeight">Min weight</FieldLabel>
          <div className="relative">
            <input
              type="number"
              id="minWeight"
              value={minWeight}
              onChange={(e) => setMinWeight(Number(e.target.value))}
              onFocus={(e) => e.target.select()}
              onBlur={(e) => {
                if (e.target.value === '' || e.target.value === null) setMinWeight(0);
              }}
              min="0"
              step="0.01"
              disabled={loading}
              className="w-full pl-3 pr-7 py-2 border border-slate-300 rounded-lg bg-surface text-sm text-slate-900 font-medium transition-colors duration-150 hover:border-slate-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
              t
            </span>
          </div>
        </div>

        {/* Route groupings (multi) */}
        <div className="flex-1 min-w-full sm:min-w-[280px]">
          <FieldLabel>Routes</FieldLabel>
          <ThemedSelect
            isMulti
            value={selectedIds}
            onChange={(e) => {
              const ids = e.target.value || [];
              const chosen = ids.map((id) => groupings.find((g) => g.id === id)).filter(Boolean);
              setSelectedGroupings(chosen);
            }}
            placeholder={loadingGroupings ? 'Loading routes…' : 'Select one or more routes…'}
            disabled={loading || loadingGroupings}
            className="w-full"
          >
            {groupings.map((grouping) => (
              <option key={grouping.id} value={grouping.id}>
                {grouping.name}
              </option>
            ))}
          </ThemedSelect>
        </div>

        {/* Action */}
        <div className="w-full sm:w-auto">
          <button
            onClick={onGenerate}
            disabled={!canGenerate}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Generating…</span>
              </>
            ) : hasResults ? (
              <>
                <FaRedoAlt className="w-3 h-3" />
                <span>Update</span>
              </>
            ) : (
              <>
                <FaPlay className="w-3 h-3" />
                <span>Generate Summary</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Single quiet caption when combining routes — the full breakdown lives in
          the empty state (pre-results) and the results strip (post-results). */}
      {selected.length > 1 && (
        <p className="mt-2 text-[11px] text-slate-500">
          {selected.length} routes combined — metrics are summed and shared locations are counted
          once.
        </p>
      )}
    </div>
  );
};

export default FilterControls;
